import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, send, update, voidRecord} from '../services/InvoicesService';
import {Invoice} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useInvoice = (invoiceId?: string) => {
    const queryClient = useQueryClient();

    const invoiceQuery = useQuery({
        queryKey: ['invoice', invoiceId],
        queryFn: () => fetch(invoiceId!),
        enabled: !!invoiceId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Invoice) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            return add(payload as Invoice);
        },
        onSuccess: () => {
            notifySuccess('Invoice created');
            queryClient.invalidateQueries({queryKey: ['invoices']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Invoice) => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return update(invoiceId, values);
        },
        onSuccess: () => {
            notifySuccess('Invoice updated');
            queryClient.invalidateQueries({queryKey: ['invoices']});
            queryClient.invalidateQueries({queryKey: ['invoice', invoiceId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return deleteRecord(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice deleted');
            queryClient.invalidateQueries({queryKey: ['invoices']});
        },
        onError: notifyError,
    });

    const sendMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return send(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice sent');
            queryClient.invalidateQueries({queryKey: ['invoices']});
            queryClient.invalidateQueries({queryKey: ['invoice', invoiceId]});
        },
        onError: notifyError,
    });

    const voidMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return voidRecord(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice voided');
            queryClient.invalidateQueries({queryKey: ['invoices']});
            queryClient.invalidateQueries({queryKey: ['invoice', invoiceId]});
        },
        onError: notifyError,
    });

    return {
        record: invoiceQuery.data,
        loading: invoiceQuery.isLoading,
        fetchRecord: invoiceQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        handleSend: sendMutation.mutate,
        handleVoid: voidMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
        sendMutation,
        voidMutation,
    };
};
